import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import * as moment from "moment";

//redux
import PropTypes from 'prop-types';

const numberFormat = new Intl.NumberFormat('es-MX', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Redondea el paso del eje a 1 / 2 / 2.5 / 5 x 10^n para que las etiquetas caigan en
// cifras redondas ($24.00, $24.50, ...) en vez de $24.63, $24.89, ...
const niceStep = (rawStep) => {
  const exponent = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const fraction = rawStep / exponent;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * exponent;
};

const formatPrice = (value) => {
  if (value === null || value === undefined || isNaN(Number(value))) return '';
  return '$' + numberFormat.format(Number(value));
};

function ComparePricesGraph(props) {
  // Los precios suelen variar por centavos: con el eje desde 0 las series se ven
  // encimadas, por eso el ajuste al rango real viene activado por omisión.
  const [fitToRange, setFitToRange] = useState(true);

  const yBounds = useMemo(() => {
    const values = (props.series || [])
      .flatMap(serie => (serie.data || []).map(point => (point && typeof point === 'object' ? point.y : point)))
      .filter(value => value !== null && value !== undefined && !isNaN(Number(value)))
      .map(Number);

    if (values.length === 0) return {};

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    // Rango cero (un solo precio o todas las series iguales): margen fijo para no
    // colapsar el eje.
    const padding = range > 0 ? range * 0.15 : Math.max(Math.abs(max) * 0.02, 0.25);
    const step = niceStep((range + padding * 2) / 5);
    const niceMin = Math.max(0, Number((Math.floor((min - padding) / step) * step).toFixed(4)));
    const niceMax = Number((Math.ceil((max + padding) / step) * step).toFixed(4));

    return {
      min: niceMin,
      max: niceMax,
      tickAmount: Math.max(1, Math.round((niceMax - niceMin) / step)),
    };
  }, [props.series]);

  const getOptions = () => {
    const period = props.period || [];
    const hasPeriod = period.length === 2 && period[0] && period[1];

    return {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        type: "datetime",
        min: hasPeriod ? moment(period[0]).startOf('day').valueOf() : undefined,
        max: hasPeriod ? moment(period[1]).endOf('day').valueOf() : undefined,
        labels: {
          format: "dd-MM",
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: fitToRange ? yBounds.min : undefined,
        max: fitToRange ? yBounds.max : undefined,
        forceNiceScale: !fitToRange,
        decimalsInFloat: 2,
        tickAmount: fitToRange ? yBounds.tickAmount : undefined,
        labels: {
          formatter: function (value) { return formatPrice(value); },
        },
      },
      dataLabels: {
        enabled: !1,
      },
      // Misma paleta del catálogo, reordenada para que dos series contiguas nunca
      // caigan en tonos parecidos. Se omiten los dos casi-blancos, invisibles sobre
      // el fondo de la tarjeta.
      colors: ['#ED5565', '#4FC1E9', '#A0D468', '#967ADC', '#F6BB42', '#37BC9B', '#5D9CEC', '#D770AD', '#E9573F', '#3BAFDA', '#8CC152', '#AC92EC', '#FFCE54', '#48CFAD', '#4A89DC', '#EC87C0', '#DA4453', '#FC6E51', '#656D78', '#AAB2BD', '#434A54', '#CCD1D9'],
      stroke: {
        curve: "straight",
        width: 2.5,
      },
      // Sin marcadores, un producto cotizado un solo día del periodo no se vería.
      markers: {
        size: 4,
        hover: { size: 6 },
      },
      legend: {
        position: "bottom",
      },
      tooltip: {
        shared: true,
        x: {
          format: "dd MMM yyyy",
        },
        y: {
          formatter: function (value) { return formatPrice(value); },
        },
      },
    };
  }

  return (
    <React.Fragment>
      <Col xl="12">
        <Card>
          <CardBody>
            <div className="clearfix">
              <div className="form-check form-switch float-end">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="fit-range-switch"
                  checked={fitToRange}
                  onChange={() => setFitToRange(!fitToRange)}
                />
                <label className="form-check-label" htmlFor="fit-range-switch">
                  Ajustar escala al rango
                </label>
              </div>
              <h4 className="card-title mb-4 text-center">Histórico de Precios</h4>
            </div>

            <Row>
              <Col lg="12">
                <div id="line-chart" dir="ltr">
                  {props.series.length > 0 && props.options.length > 0 &&
                    <ReactApexChart
                      series={props.series}
                      options={getOptions()}
                      height={320}
                      className="apex-charts"
                    />
                  }
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
}

ComparePricesGraph.propTypes = {
  series: PropTypes.array,
  options: PropTypes.array,
  period: PropTypes.array,
}

export default ComparePricesGraph;
